import { SideSheetBodyContainer, TOAST_VARIANTS } from "@cdkglobal/radial";
import {
  StyledSideSheetMainWrapper,
  StyledPageWrapper,
  StyledStickyHeaderContainer,
} from "../../utils/styled";
import LandingPageHeader from "./components/landing-page-header/LandingPageHeader";
import Toaster from "../common/Toaster";
import RecordSideSheet from "./components/side-sheet/RecordSideSheet";
import { useIntl } from "react-intl";
import { SetupsDashboardNavHeader } from "./components/setups-dashboard/SetupsDashboardNavHeader";
import { useLandingPageState } from "../../hooks/useLandingPageState";
import { useRefetchTableData } from "../../hooks";
import { useUserContext } from "../../contexts/UserContext";
import { useUIStateContext } from "../../contexts/UIStateContext";
import { useRowDataContext } from "../../contexts/RowDataContext";
import { SetupsDashboardNavTabs } from "./components/setups-dashboard/SetupsDashboardNavTabs";
import { LandingPageProps } from "../../types";
import LandingPageContent from "./LandingPageContent";
import { DEFAULT_TOAST } from "./constants";

export default function LandingPage({
  navigationTabs = <SetupsDashboardNavTabs />,
}: Readonly<LandingPageProps>): JSX.Element {
  const { formatMessage } = useIntl();
  const { rowDataReducerState, dispatch } = useRowDataContext();

  const {
    selected,
    iamUserState,
    evaluatedResults,
    setEvaluatedResults,
    setupListNavInfoByDepartment,
    setupBaseRequestInfo,
    setTableTitleInfo,
    headerLabel,
    setSelected,
  } = useUserContext();

  const {
    setSearchValue,
    setCurrentPage,
    setPageSizeOption,
    setIsRecordOpen,
    setActiveTabIndex,
    setSelectedTabSectionKey,
    setSelectedDashboardTabSectionKey,
    setIsSideSheetOpen,
    setLandingPageToast,
    setChipFilterValue,
  } = useUIStateContext();

  useRefetchTableData({
    setupBaseRequestInfo,
    headerLabel,
    landingPageOptions: selected,
    iamUserState,
    evaluatedResults,
    setupListNavInfoByDepartment,
    dispatch,
    setTableTitleInfo,
    setCurrentPage,
    setSearchValue,
    setPageSizeOption,
    setIsRecordOpen,
    setActiveTabIndex,
    setSelectedTabSectionKey,
    setSelectedDashboardTabSectionKey,
    setEvaluatedResults,
    setIsSideSheetOpen,
    setLandingPageToast,
    setSelected,
    setChipFilterValue,
  });

  const {
    isRecordOpen,
    isSideSheetOpen,
    landingPageToast,
    isFetchingUserData,
    isSetupSelected,
    isLoading,
    shouldShowEmptyState,
    showNavHeader,
    showNavTabs,
    showLandingPageToast,
    showSideSheet,
  } = useLandingPageState(rowDataReducerState);

  return (
    <StyledSideSheetMainWrapper isModal>
      <SideSheetBodyContainer>
        <StyledPageWrapper>
          {showLandingPageToast && (
            <Toaster
              variant={landingPageToast.variant}
              message={landingPageToast.message}
              clearMessage={() => setLandingPageToast(DEFAULT_TOAST)}
              position="fixed"
              actionText={formatMessage({
                id: "action.dismiss",
                defaultMessage: "Dismiss",
              })}
              noActionButton={
                landingPageToast.variant === TOAST_VARIANTS.NEUTRAL
              }
            />
          )}

          <StyledStickyHeaderContainer>
            <LandingPageHeader
              rowDataReducerState={rowDataReducerState}
              dispatch={dispatch}
            />

            {showNavTabs && navigationTabs}
          </StyledStickyHeaderContainer>

          {showNavHeader && (
            <SetupsDashboardNavHeader
              rowDataReducerState={rowDataReducerState}
              dispatch={dispatch}
            />
          )}

          <LandingPageContent
            isFetchingUserData={isFetchingUserData}
            isLoading={isLoading}
            isSetupSelected={isSetupSelected}
            isRecordOpen={isRecordOpen}
            shouldShowEmptyState={shouldShowEmptyState}
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
          />
        </StyledPageWrapper>
      </SideSheetBodyContainer>

      {showSideSheet && (
        <RecordSideSheet
          isSideSheetOpen={isSideSheetOpen}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      )}
    </StyledSideSheetMainWrapper>
  );
}
